import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Pagination,
  PaginationItem,
} from '@mui/material';

import { TableHeadCell } from '../table-cells/TableCells';
import CollectionsListItem from './CollectionsListItem';
import FetchError from '../UI/fetch-error/FetchError';

import './Collections.scss';

import { PreviousPageIcon, NextPageIcon } from '../../PaginationIcons';

import {
  useGetAuthenticationQuery,
  useGetCollectionsQuery,
  useUpdateCollectionPrioritiesMutation,
} from '../../services/ADapi';

const initialDragAndDropState = {
  draggedFrom: null,
  draggedTo: null,
  isDragging: false,
  originalOrder: [],
  updatedOrder: [],
};

const CollectionsList = ({ searchQuery }) => {
  const [page, setPage] = useState(1);
  const {
    data: { user_id },
  } = useGetAuthenticationQuery();
  const { data: collections = [], isError } = useGetCollectionsQuery(user_id);
  const [collectionsList, setCollectionsList] = useState(collections);
  const [dragAndDrop, setDragAndDrop] = useState(initialDragAndDropState);

  const [updateCollectionPriorities] = useUpdateCollectionPrioritiesMutation();

  const priority = useRef();

  useEffect(() => {
    const sortedCollections = [...collections].sort(
      (a, b) => a.priority - b.priority
    );

    setCollectionsList(sortedCollections);
  }, [collections]);

  useEffect(() => {
    priority.current = collectionsList.map((item, index) => {
      return {
        collection_id: item.id,
        priority: index + 1,
      };
    });
  }, [collectionsList]);

  const ITEMS_PER_PAGE = 5;
  const paginatedCollections = collectionsList.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const pageHandler = (_e, value) => {
    setPage(value);
  };

  const changePriorityHandler = async () => {
    try {
      await updateCollectionPriorities({
        user_id,
        payload: { data: priority.current },
      }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const filteredCollections = paginatedCollections.filter((collection) => {
    if (!searchQuery) {
      return true;
    }

    const query = searchQuery.toLowerCase();

    return collection.name.toLowerCase().includes(query);
  });

  const onDragStart = (e) => {
    const initialPosition = Number(e.currentTarget.dataset.position);

    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: initialPosition,
      isDragging: true,
      originalOrder: collectionsList,
    });

    e.dataTransfer.setData('text/html', '');
  };

  const onDragOver = (e) => {
    e.preventDefault();

    let newList = dragAndDrop.originalOrder;

    const draggedFrom = dragAndDrop.draggedFrom;
    const draggedTo = Number(e.currentTarget.dataset.position);
    const itemDragged = newList[draggedFrom];
    const remainingItems = newList.filter(
      (_item, index) => index !== draggedFrom
    );

    newList = [
      ...remainingItems.slice(0, draggedTo),
      itemDragged,
      ...remainingItems.slice(draggedTo),
    ];

    if (draggedTo !== dragAndDrop.draggedTo) {
      setDragAndDrop({
        ...dragAndDrop,
        updatedOrder: newList,
        draggedTo: draggedTo,
      });
    }
  };

  const onDrop = (e) => {
    setCollectionsList(dragAndDrop.updatedOrder);

    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: null,
      draggedTo: null,
      isDragging: false,
    });
  };

  const onDragLeave = () => {
    setDragAndDrop({
      ...dragAndDrop,
      draggedTo: null,
    });
  };

  if (isError) {
    return <FetchError />;
  }

  if (collectionsList.length === 0) {
    return <p className="collections-list-empty">No Collections yet</p>;
  }

  return (
    <React.Fragment>
      <TableContainer sx={{ pt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell></TableHeadCell>
              <TableHeadCell align="center">Priority</TableHeadCell>
              <TableHeadCell>Collections</TableHeadCell>
              <TableHeadCell>Policy</TableHeadCell>
              <TableHeadCell>Users</TableHeadCell>
              <TableHeadCell></TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCollections.map(
              ({ id, name, priority, profile, ad_users }, index) => (
                <CollectionsListItem
                  key={id}
                  id={id}
                  priority={priority}
                  name={name}
                  profile={profile}
                  users={ad_users.length}
                  dataposition={index}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragLeave={onDragLeave}
                  onChangePriority={changePriorityHandler}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {collections.length > ITEMS_PER_PAGE ? (
        <Grid container sx={{ justifyContent: 'flex-end', pt: 5 }}>
          <Pagination
            count={Math.ceil(collections.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={pageHandler}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                slots={{
                  next: NextPageIcon,
                  previous: PreviousPageIcon,
                }}
              />
            )}
          />
        </Grid>
      ) : null}
    </React.Fragment>
  );
};

export default CollectionsList;
